import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TrendingUp, TrendingDown, DollarSign, Plus, X,
    Trash2, Edit2,
} from 'lucide-react';
import SearchBar from './SearchBar';
import ExportButton from './ExportButton';
import { filterBySearch, sortData, formatDate } from '../../utils/adminUtils';

const FinancePage = ({ showToast }) => {
    const [transactions, setTransactions] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [editingTransaction, setEditingTransaction] = useState(null);

    const [formData, setFormData] = useState({
        type: 'income',
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    const categories = {
        income: ['Development', 'Design', 'Consulting', 'Sales', 'Services', 'Other'],
        expense: ['Hardware', 'Software', 'Subscriptions', 'Marketing', 'Office', 'Other']
    };

    useEffect(() => {
        loadTransactions();
    }, []);

    const loadTransactions = () => {
        const saved = JSON.parse(localStorage.getItem('finance_transactions') || '[]');
        setTransactions(saved);
    };

    const calculateStats = () => {
        const income = transactions
            .filter(t => t.type === 'income')
            .reduce((sum, t) => sum + parseFloat(t.amount), 0);

        const expenses = transactions
            .filter(t => t.type === 'expense')
            .reduce((sum, t) => sum + parseFloat(t.amount), 0);

        return {
            totalIncome: income,
            totalExpenses: expenses,
            balance: income - expenses
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const transaction = {
            ...formData,
            id: editingTransaction ? editingTransaction.id : Date.now(),
            amount: parseFloat(formData.amount),
            createdAt: editingTransaction ? editingTransaction.createdAt : Date.now()
        };

        let updated;
        if (editingTransaction) {
            updated = transactions.map(t => t.id === transaction.id ? transaction : t);
            showToast?.('Transaction updated successfully', 'success');
        } else {
            updated = [transaction, ...transactions];
            showToast?.('Transaction added successfully', 'success');
        }

        localStorage.setItem('finance_transactions', JSON.stringify(updated));
        setTransactions(updated);
        resetForm();
    };

    const handleDelete = (id) => {
        if (window.confirm('Delete this transaction?')) {
            const updated = transactions.filter(t => t.id !== id);
            localStorage.setItem('finance_transactions', JSON.stringify(updated));
            setTransactions(updated);
            showToast?.('Transaction deleted', 'success');
        }
    };

    const handleEdit = (transaction) => {
        setEditingTransaction(transaction);
        setFormData({
            type: transaction.type,
            description: transaction.description,
            amount: transaction.amount.toString(),
            category: transaction.category,
            date: transaction.date,
            notes: transaction.notes || ''
        });
        setShowAddModal(true);
    };

    const resetForm = () => {
        setFormData({
            type: 'income',
            description: '',
            amount: '',
            category: '',
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setEditingTransaction(null);
        setShowAddModal(false);
    };

    const getFilteredTransactions = () => {
        let filtered = [...transactions];

        // Search filter
        if (searchQuery) {
            filtered = filterBySearch(filtered, searchQuery, ['description', 'category', 'notes']);
        }

        // Type filter
        if (typeFilter !== 'all') {
            filtered = filtered.filter(t => t.type === typeFilter);
        }

        // Category filter
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(t => t.category === categoryFilter);
        }

        // Sort by date (newest first)
        filtered = sortData(filtered, 'date', 'desc');

        return filtered;
    };

    const stats = calculateStats();
    const filtered = getFilteredTransactions();

    // Get all unique categories
    const allCategories = [...new Set(transactions.map(t => t.category))];

    return (
        <div className="space-y-8">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-6 rounded-2xl"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-emerald-400" />
                        </div>
                        <span className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Income</span>
                    </div>
                    <h3 className="text-3xl font-bold text-black mb-1">₹{stats.totalIncome.toLocaleString()}</h3>
                    <p className="text-emerald-400/100 font-bold text-sm">Total earnings</p>
                </motion.div>

                <motion.div
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20 p-6 rounded-2xl"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                            <TrendingDown className="w-6 h-6 text-red-400" />
                        </div>
                        <span className="text-xs text-red-400 font-bold uppercase tracking-wider">Expenses</span>
                    </div>
                    <h3 className="text-3xl font-bold text-black mb-1">₹{stats.totalExpenses.toLocaleString()}</h3>
                    <p className="text-red-400/100 font-bold text-sm">Total spending</p>
                </motion.div>

                <motion.div
                    whileHover={{ y: -5 }}
                    className={`bg-gradient-to-br ${stats.balance >= 0
                            ? 'from-brand-teal/10 to-brand-teal/5 border-brand-teal/20'
                            : 'from-yellow-500/10 to-yellow-500/5 border-yellow-500/20'
                        } border p-6 rounded-2xl`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 ${stats.balance >= 0 ? 'bg-brand-teal/20' : 'bg-yellow-500/20'
                            } rounded-xl flex items-center justify-center`}>
                            <DollarSign className={`w-6 h-6 ${stats.balance >= 0 ? 'text-brand-teal' : 'text-yellow-400'
                                }`} />
                        </div>
                        <span className={`text-xs ${stats.balance >= 0 ? 'text-brand-teal' : 'text-yellow-400'
                            } font-bold uppercase tracking-wider`}>Balance</span>
                    </div>
                    <h3 className="text-3xl font-bold text-black mb-1">₹{Math.abs(stats.balance).toLocaleString()}</h3>
                    <p className={`${stats.balance >= 0 ? 'text-brand-teal/100' : 'text-yellow-400/60'
                        } font-bold text-sm`}>
                        {stats.balance >= 0 ? 'Net profit' : 'Net loss'}
                    </p>
                </motion.div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="w-full sm:w-96">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search transactions..."
                    />
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-4 py-2 bg-white/5 border border-black/10 rounded-xl text-Black text-sm focus:border-brand-teal focus:outline-none"
                    >
                        <option value="all" className="bg-[#ffffff]">All Types</option>
                        <option value="income" className="bg-[#ffffff]">Income</option>
                        <option value="expense" className="bg-[#ffffff]">Expense</option>
                    </select>

                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="px-4 py-2 bg-white/5 border border-black/10 rounded-xl text-Black text-sm focus:border-brand-teal focus:outline-none"
                    >
                        <option value="all" className="bg-[#ffffff]">All Categories</option>
                        {allCategories.map(cat => (
                            <option key={cat} value={cat} className="bg-[#ffffff]">{cat}</option>
                        ))}
                    </select>

                    <ExportButton data={filtered} filename="transactions" label="Export" />

                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-brand-teal text-[#0a0a0a] rounded-xl font-bold hover:bg-[#00d1c1] transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        Add Transaction
                    </button>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-white/5 text-gray-400 text-xs uppercase tracking-widest">
                                <th className="px-6 py-4 font-semibold">Date</th>
                                <th className="px-6 py-4 font-semibold">Type</th>
                                <th className="px-6 py-4 font-semibold">Description</th>
                                <th className="px-6 py-4 font-semibold">Category</th>
                                <th className="px-6 py-4 font-semibold text-right">Amount</th>
                                <th className="px-6 py-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {filtered.map((transaction) => (
                                <tr key={transaction.id} className="hover:bg-white/[0.02] transition-colors">
                                    <td className="px-6 py-4 text-sm text-gray-400">
                                        {formatDate(transaction.date)}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${transaction.type === 'income'
                                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                            }`}>
                                            {transaction.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-white">{transaction.description}</div>
                                        {transaction.notes && (
                                            <div className="text-xs text-gray-500 mt-1">{transaction.notes}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-white/5 border border-white/5 rounded text-xs text-gray-400">
                                            {transaction.category}
                                        </span>
                                    </td>
                                    <td className={`px-6 py-4 text-right font-bold ${transaction.type === 'income' ? 'text-emerald-400' : 'text-red-400'
                                        }`}>
                                        {transaction.type === 'income' ? '+' : '-'}₹{transaction.amount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(transaction)}
                                                className="p-2 text-gray-500 hover:text-brand-teal transition-colors"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(transaction.id)}
                                                className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            {searchQuery || typeFilter !== 'all' || categoryFilter !== 'all'
                                ? 'No transactions match your filters'
                                : 'No transactions yet. Click "Add Transaction" to get started.'}
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Transaction Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={resetForm}
                            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        />

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-[32px] p-8"
                        >
                            <button
                                onClick={resetForm}
                                className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <h2 className="text-2xl font-bold text-white mb-8">
                                {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Type Selection */}
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'income', category: '' })}
                                        className={`p-4 rounded-xl border-2 transition-all ${formData.type === 'income'
                                                ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                                : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                                            }`}
                                    >
                                        <TrendingUp className="w-6 h-6 mx-auto mb-2" />
                                        <span className="font-bold">Income</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'expense', category: '' })}
                                        className={`p-4 rounded-xl border-2 transition-all ${formData.type === 'expense'
                                                ? 'border-red-500 bg-red-500/10 text-red-400'
                                                : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                                            }`}
                                    >
                                        <TrendingDown className="w-6 h-6 mx-auto mb-2" />
                                        <span className="font-bold">Expense</span>
                                    </button>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-brand-teal focus:outline-none"
                                        placeholder="e.g., Develop API, Buy RAM"
                                    />
                                </div>

                                {/* Amount and Category */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Amount (₹) *</label>
                                        <input
                                            type="number"
                                            required
                                            min="0"
                                            step="0.01"
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-brand-teal focus:outline-none"
                                            placeholder="50000"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
                                        <select
                                            required
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-brand-teal focus:outline-none"
                                        >
                                            <option value="" className="bg-[#0a0a0a]">Select category</option>
                                            {categories[formData.type].map(cat => (
                                                <option key={cat} value={cat} className="bg-[#0a0a0a]">{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Date *
                                    </label>

                                    <div className="relative">
                                        <input
                                            type="date"
                                            required
                                            value={formData.date}
                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-brand-teal focus:outline-none appearance-none"
                                        />

                                        {/* CUSTOM WHITE ICON */}
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white">
                                            📅
                                        </div>
                                    </div>
                                </div>

                                {/* Notes */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Notes (Optional)</label>
                                    <textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-brand-teal focus:outline-none resize-none"
                                        rows="3"
                                        placeholder="Additional details..."
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="w-full py-4 bg-brand-teal text-[#0a0a0a] font-bold rounded-xl hover:bg-[#00d1c1] transition-all"
                                >
                                    {editingTransaction ? 'Update Transaction' : 'Add Transaction'}
                                </button>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FinancePage;
